import { useEffect, useRef, useState } from 'react';

import type { Message } from './types';

interface ResumedChat {
  chatId: number;
  messages: Message[];
}

interface Options {
  open: boolean;
  loadChat: () => Promise<ResumedChat | null>;
  onResume: (resumed: ResumedChat) => void;
  hasStarted: () => boolean;
}

export function useChatResume({ open, loadChat, onResume, hasStarted }: Options): boolean {
  const [resuming, setResuming] = useState(false);
  // Guards the fetch to a single attempt per mount; the effect otherwise re-runs
  // whenever the callback props change identity (every render).
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (!open || hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;
    setResuming(true);

    void (async () => {
      try {
        const resumed = await loadChat();
        if (resumed && !hasStarted()) onResume(resumed);
      } catch {
        // Resume is best-effort; on failure the sidebar just starts fresh.
      } finally {
        setResuming(false);
      }
    })();
  }, [open, loadChat, onResume, hasStarted]);

  return resuming;
}
